/* eslint-disable spire/fenced-imports */

import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import { formatPhoneNumber } from "@tKgpco/Common/Utilities/formatPhoneNumber";
import { checkoutShippingCarrierServiceStyles } from "@tKgpco/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitCarrierService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setExpeditedUserNameError from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/ExpeditedUserNameErrorHandler";
import setExpeditedUserName from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/ExpeditedUserNameHandler";
import setExpeditedUserPhoneError from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/ExpeditedUserPhoneErrorHandler";
import setExpeditedUserphone from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/ExpeditedUserPhoneHandler";
import setReleaseNumber from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/ReleaseNumberHandler";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const cartState = getCurrentCartState(state).value;
    return {
        cartState,
        releaseNumber: state.KGPState.CheckoutReview.releaseNumber,
        expeditedUserNameError: state.KGPState.CheckoutReview.expeditedUserNameError,
        expeditedUserPhoneError: state.KGPState.CheckoutReview.expeditedUserPhoneError,
        isAwaitingApproval: cartState?.isAwaitingApproval,
        userNameDetail:
            cartState?.properties.cartContactName ||
            `${state.context.session.firstName} ${state.context.session.lastName}`,
        phone: cartState?.properties.cartContactPhone || state.context.session.properties.userPhone,
    };
};

const mapDispatchToProps = {
    setReleaseNumber,
    setExpeditedUserName,
    setExpeditedUserphone,
    setExpeditedUserNameError,
    setExpeditedUserPhoneError,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderExpeditorStyles {
    container?: GridContainerProps;
    fieldsItem?: GridItemProps;
}

export const orderExpeditorStyles: OrderExpeditorStyles = {
    fieldsItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    container: {
        css: css`
            margin-top: 15px;
        `,
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const orderExpeditorStylesMain = orderExpeditorStyles;
const styles = orderExpeditorStyles;

const OrderExpeditorDetails: FC<Props> = ({
    userNameDetail,
    phone,
    expeditedUserPhoneError,
    expeditedUserNameError,
    setExpeditedUserName,
    setExpeditedUserphone,
    setExpeditedUserNameError,
    setExpeditedUserPhoneError,
    isAwaitingApproval,
}) => {
    const [userName, setUserName] = useState("");
    const [userPhone, setUserPhone] = useState("");

    const forbiddenChars = /[^a-zA-Z0-9\s]/;
    // Check for overly long spacing (more than one consecutive space)
    const excessiveSpacing = / {2,}/;

    useEffect(() => {
        setUserName(userNameDetail || "");
        setUserPhone(phone || "");
    }, [userNameDetail, phone]);

    useEffect(() => {
        setExpeditedUserName({
            expeditedUserName: userName,
        });

        setExpeditedUserphone({
            expeditedUserphone: userPhone,
        });
    }, [userName, userPhone]);

    const userNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserName(event.currentTarget.value);
        validateExpeditedUserName(event.currentTarget.value);
    };
    const userPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const trimmedPhone = event.target.value?.split("-")?.join("");
        if (trimmedPhone.length <= 10) {
            setUserPhone(event.currentTarget.value);
            validateExpeditedUserPhone(event.currentTarget.value);
        }
    };

    const validateExpeditedUserName = (value = "") => {
        const error = !value
            ? `Name is required`
            : forbiddenChars.test(value)
              ? `Special Characters Not Allowed`
              : excessiveSpacing.test(value)
                ? `Excessive Spacing Not Allowed`
                : "";
        setExpeditedUserNameError({ expeditedUserNameError: error });
    };

    const validateExpeditedUserPhone = (value = "") => {
        const trimmedPhone = value?.split("-")?.join("");
        const error = !trimmedPhone
            ? `Phone Number is required`
            : !Number(trimmedPhone) || trimmedPhone.length !== 10
              ? `Enter Valid Phone Number`
              : "";
        setExpeditedUserPhoneError({ expeditedUserPhoneError: error });
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...checkoutShippingCarrierServiceStyles.shippingInformationTitleGridItem}>
                <Typography {...checkoutShippingCarrierServiceStyles.shippingMethod} as="h4">
                    {translate("Order Expeditor Contact Details")}
                </Typography>
            </GridItem>
            <GridItem {...styles.fieldsItem}>
                <TextField
                    label={translate("Order (Expeditor) Contact")}
                    required
                    value={userName}
                    onChange={userNameChange}
                    error={expeditedUserNameError}
                    disabled={isAwaitingApproval}
                />
            </GridItem>
            <GridItem {...styles.fieldsItem}>
                <TextField
                    label={translate("Order (Expeditor) Phone #")}
                    required
                    value={formatPhoneNumber(userPhone)}
                    onChange={userPhoneChange}
                    error={expeditedUserPhoneError}
                    disabled={isAwaitingApproval}
                />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderExpeditorDetails),
    definition: {
        displayName: "Expeditor Details",
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
    },
};

export default widgetModule;
